import Immutable from 'immutable';
import { handleActions } from 'redux-actions';
import { TYPES as ANNUAL_AVAILABILITY_TYPES } from 'actions/annualAvailabilityActions';
import { TYPES as CALENDAR_OFFER_TYPES } from 'actions/calendarOfferActions';
import { checkForNestedProperty, getNumberOfInfants } from 'utils/utils';
import { TYPES as BASE_STATE_TYPES } from 'actions/baseStateActions';
import { TYPES as SEARCH_RESULTS_TYPES } from 'actions/searchResultsActions';
import { TYPES as HOTEL_DETAILS_TYPES } from 'actions/hotelDetailsActions';

const initialState = Immutable.Map();

export function calculateNumberOfChargeablePassengers(response) {
  const numberOfAdults = response.getIn(['queryState', 'props', 'nAdt', 'value']);
  const childrenArray = response.getIn(['queryState', 'props', 'cdn', 'arrayValue'], Immutable.List());
  const numberOfChildren = childrenArray.size - getNumberOfInfants(childrenArray);

  return response.set('numberOfChargeablePassengers', numberOfAdults + numberOfChildren);
}

export function removeGroupedDimensionsByType(state) {
  const groups = [
    'D_Destination',
    'D_DepartureAirport',
    'D_BoardBasis',
    'D_Nights',
    'D_RoomConfiguration',
    'D_HolidayType',
    'D_DayOfMonth',
    'D_PCM_Hotel_Facilities',
    'D_PCM_Pool',
    'D_PCM_Kids',
  ];
  return state.withMutations((mutatedState) => {
    groups.forEach(group => mutatedState.delete(group));
  });
}

/* istanbul ignore next */
export function groupDimensionsByType(action, baseStateDimensions) {
  const dimensionsByType = {};
  baseStateDimensions.map((dimId) => {
    const dimensionEntity = action.response.entities.dimensions[dimId];
    const dimensionType = dimensionEntity.dimension;

    if (!dimensionsByType[dimensionType]) {
      dimensionsByType[dimensionType] = [];
    }

    dimensionsByType[dimensionType].push(dimId);
  });

  return dimensionsByType;
}

function deleteFieldKey(state, action) {
  const field = state.get(action.field).filter(id => id !== Number(action.id));
  return state.set(action.field, field);
}

function addFieldKey(state, action) {
  const field = state.get(action.field);
  return state.get(action.field) ?
    state.setIn([action.field, field.size], Number(action.id)) :
    state.set(action.field, Immutable.fromJS([Number(action.id)]));
}

function changeFieldKey(state, action) {
  return action.addition ?
    addFieldKey(state, action) :
    deleteFieldKey(state, action);
}

function changeNightFlex(state, action) {
  return state.setIn(['queryState', 'props', 'nightFlex', 'set'], action.value);
}

/* istanbul ignore next */
function reducerCallback(state, action) {
  let response = state;

  if (checkForNestedProperty(action, 'response.result.baseState')) {
    const baseState = action.response.result.baseState;
    response = removeGroupedDimensionsByType(state);
    response = response.merge(baseState, groupDimensionsByType(action, baseState.dimensions));
    response = calculateNumberOfChargeablePassengers(response);
  }

  return response;
}

function filterCallback(state, action) {
  if (action.response.result && action.response.result.baseState) {
    delete action.response.result.baseState.pageStart;
    delete action.response.result.baseState.numAggregateRecs;
  }

  return reducerCallback(state, action);
}

/* istanbul ignore next */
export default handleActions({
  [ANNUAL_AVAILABILITY_TYPES.ANNUAL_AVAILABILITY_SUCCESS]: filterCallback,
  [SEARCH_RESULTS_TYPES.SEARCH_RESULTS_SUCCESS]: reducerCallback,
  [HOTEL_DETAILS_TYPES.HOTEL_DETAILS_SUCCESS]: reducerCallback,
  [BASE_STATE_TYPES.SET_ROOMS_VALIDITY]: (state, action) => state.set('areRoomsValid', action.isValid),
  [BASE_STATE_TYPES.BASE_STATE_SUCCESS]: filterCallback,
  [BASE_STATE_TYPES.DELETE_BASESTATE_FIELD_KEY]: deleteFieldKey,
  [BASE_STATE_TYPES.CHANGE_BASESTATE_FIELD_KEY]: changeFieldKey,
  [BASE_STATE_TYPES.CHANGE_NIGHT_FLEX]: changeNightFlex,
  [BASE_STATE_TYPES.UPDATE_CAL_DATE]: (state, action) => {
    let response = state;
    response = response.setIn(['queryState', 'props', 'calDate', 'value'], Number(action.calDate));
    response = response.setIn(['queryState', 'props', 'calDate', 'selected'], true);
    response = response.setIn(['queryState', 'props', 'deptDate', 'value'], Number(action.calDate));
    return response;
  },
  [BASE_STATE_TYPES.UPDATE_PARTY]: (state, action) => {
    let response = state;
    let nAdt = 0;
    let cdn = [];

    const rooms = action.rooms.map(room => ({
      cdn: {
        arrayValue: room.children ? room.children.split(',').map(child => Number(child)) : [],
      },
      nAdt: {
        value: room.adults,
      },
    }));

    action.rooms.forEach((room) => {
      nAdt += room.adults;
      cdn = room.children ? cdn.concat(room.children.split(',')) : cdn;
    });

    response = response.setIn(['queryState', 'props', 'rooms'], Immutable.fromJS(rooms));
    response = response.setIn(['queryState', 'props', 'nAdt', 'value'], nAdt);
    response = response.setIn(['queryState', 'props', 'cdn', 'arrayValue'], Immutable.fromJS(cdn.map(child => Number(child))));
    response = calculateNumberOfChargeablePassengers(response);
    return response;
  },
  [BASE_STATE_TYPES.CHANGE_NUMBER_OF_CHILDREN]: (state, action) => {
    let response = state;

    const childAges = state
      .getIn(['queryState', 'props', 'rooms', action.roomNum, 'cdn', 'arrayValue'], Immutable.List()).toJS();

    const unsetChildAges = new Array(action.numChildren).fill('-1');
    const newChildAges = unsetChildAges
      .map((element, i) => childAges[i] !== undefined ? childAges[i] : element);

    response = response
      .setIn(['queryState', 'props', 'rooms', action.roomNum, 'cdn', 'arrayValue'], Immutable.fromJS(newChildAges));

    response = calculateNumberOfChargeablePassengers(response);
    return response;
  },
  [CALENDAR_OFFER_TYPES.CALENDAR_OFFERS_SUCCESS]: (state, action) => {
    let response = state;
    if (checkForNestedProperty(action, 'response.result.json.baseState')) {
      const baseState = action.response.result.json.baseState;
      delete baseState.pageStart;
      delete baseState.numAggregateRecs;

      response = removeGroupedDimensionsByType(state);
      response = response.merge(baseState, groupDimensionsByType(action, baseState.dimensions));
      response = calculateNumberOfChargeablePassengers(response);
    }
    return response;
  },
  [ANNUAL_AVAILABILITY_TYPES.CHANGE_CALDATE_VALUE]: (state, action) => state
      .set('calDateValue', action.calDate),
  [BASE_STATE_TYPES.CHANGE_NUMBER_OF_ADULTS]: (state, action) => {
    let response = state;
    response = response.setIn(['queryState', 'props', 'rooms', action.roomNum, 'nAdt', 'value'], Number(action.numAdults));
    response = calculateNumberOfChargeablePassengers(response);

    return response;
  },
  [BASE_STATE_TYPES.CHANGE_CHILD_AGE]: (state, action) => {
    let response = state;
    response = response.setIn(['queryState', 'props', 'rooms', action.roomNum, 'cdn', 'arrayValue', action.childNumber], action.age);
    response = calculateNumberOfChargeablePassengers(response);

    return response;
  },
  [ANNUAL_AVAILABILITY_TYPES.CHANGE_NUMBER_OF_NIGHTS]: (state, action) => state
    .setIn(['D_Nights', 0], action.addDimId),
}, initialState);



/** WEBPACK FOOTER **
 ** ./src/redux/reducers/baseStateReducer.js
 **/